<template>
  <div v-if="showEditor" :key="form.id || 'new'" class="editor-overlay">
    <div class="editor-panel">
      <!-- ── Header ────────────────────────────────── -->
      <div class="editor-header">
        <div class="editor-header__left">
          <span class="ai-star">✦</span>
          <h2>
            {{
              form.id
                ? $t("message.forms_page.edit")
                : $t("message.forms_page.new_form")
            }}
          </h2>
        </div>
        <button class="close-btn" @click="$emit('close')">×</button>
      </div>

      <!-- ── Two-column body ───────────────────────── -->
      <div class="editor-columns">
        <!-- LEFT: AI Chat -->
        <div class="ai-column">
          <div class="ai-col-header">
            <span class="ai-star">✦</span>
            <span>AI Assistant</span>
            <span class="ai-model-tag">Claude Haiku</span>
          </div>

          <div class="ai-messages" ref="aiMessages">
            <!-- Empty state -->
            <div v-if="aiMessages.length === 0" class="ai-empty">
              <div class="ai-empty-icon">✦</div>
              <p>
                {{
                  $t("message.forms_page.ai_hint") ||
                    "Describe the form you want, or ask to modify the current one."
                }}
              </p>
              <div class="ai-suggestions">
                <button
                  v-for="s in suggestions"
                  :key="s"
                  class="ai-suggestion"
                  @click="aiPrompt = s"
                >
                  {{ s }}
                </button>
              </div>
            </div>

            <!-- Message history -->
            <div
              v-for="(msg, i) in aiMessages"
              :key="i"
              :class="['ai-msg', 'ai-msg--' + msg.role]"
            >
              <div class="ai-msg__bubble">{{ msg.content }}</div>
              <div class="ai-msg__time">{{ msg.time }}</div>
            </div>

            <!-- Typing indicator -->
            <div v-if="aiLoading" class="ai-msg ai-msg--assistant">
              <div class="ai-msg__bubble ai-typing">
                <span /><span /><span />
              </div>
            </div>
          </div>

          <div class="ai-input-area">
            <div v-if="aiError" class="ai-error">{{ aiError }}</div>
            <textarea
              v-model="aiPrompt"
              class="ai-input"
              rows="3"
              :placeholder="
                $t('message.forms_page.ai_placeholder') ||
                  'Describe or modify the form... (Ctrl+Enter to send)'
              "
              @keydown.ctrl.enter.prevent="generateWithAI"
            />
            <button
              class="btn-ai-send"
              :disabled="aiLoading || !aiPrompt.trim()"
              @click="generateWithAI"
            >
              <span v-if="!aiLoading">
                <span class="ai-star">✦</span> Send
              </span>
              <span v-else class="ai-loading-text">
                <span class="ai-spinner" />
              </span>
            </button>
          </div>
        </div>

        <!-- RIGHT: Form Editor -->
        <div class="form-column">
          <div class="form-scroll">
            <!-- Form name -->
            <div class="form-group">
              <label>{{ $t("message.forms_page.form_name") }}</label>
              <input
                v-model="form.name"
                type="text"
                class="input-field"
                :placeholder="$t('message.forms_page.form_name')"
              />
            </div>

            <!-- JSON toggle -->
            <div class="json-editor-section">
              <label class="json-toggle">
                <input type="checkbox" v-model="showJSONEditor" />
                {{ $t("message.forms_page.edit_json") || "Edit JSON" }}
              </label>
            </div>

            <div v-if="showJSONEditor">
              <div class="form-group">
                <label>{{
                  $t("message.forms_page.form_json") || "Form JSON"
                }}</label>
                <textarea
                  v-model="jsonEditorText"
                  class="json-textarea"
                  placeholder="Paste or edit JSON here..."
                />
                <button class="btn-secondary-button" @click="loadJSONFromText">
                  {{ $t("message.forms_page.load_json") || "Load JSON" }}
                </button>
              </div>
            </div>

            <div v-else>
              <!-- Language -->
              <div class="form-group">
                <label>{{
                  $t("message.forms_page.form_language") || "Form Language"
                }}</label>
                <select v-model="form.definition.language" class="select-field">
                  <option value="en">English</option>
                  <option value="es">Spanish / Español</option>
                  <option value="pt">Portuguese / Português</option>
                  <option value="fr">French / Français</option>
                  <option value="zh">Chinese / 中文</option>
                  <option value="ar">Arabic / العربية</option>
                  <option value="hi">Hindi / हिन्दी</option>
                </select>
              </div>

              <!-- Terms -->
              <div class="form-group">
                <label>{{
                  $t("message.forms_page.terms_conditions") ||
                    "Terms & Conditions"
                }}</label>
                <textarea
                  v-model="form.definition.termsConditions"
                  class="input-field textarea-field"
                  :placeholder="
                    $t('message.forms_page.terms_placeholder') ||
                      'Enter terms and conditions...'
                  "
                  rows="3"
                />
              </div>

              <!-- Fields -->
              <div class="fields-section">
                <h3>{{ $t("message.forms_page.fields") }}</h3>
                <div
                  v-for="(field, index) in form.definition.fields"
                  :key="index"
                  class="field-row"
                >
                  <div class="field-row__top">
                    <div class="field-item">
                      <input
                        v-model="field.label"
                        type="text"
                        class="input-field"
                        :placeholder="$t('message.forms_page.field_label')"
                      />
                    </div>
                    <div class="field-item field-item--type">
                      <select v-model="field.type" class="select-field">
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="textarea">Textarea</option>
                        <option value="select">Dropdown</option>
                        <option value="multiselect">Multi-Select</option>
                        <option value="language">Language</option>
                      </select>
                    </div>
                    <label class="checkbox-label">
                      <input v-model="field.required" type="checkbox" />
                      {{ $t("message.forms_page.field_required") }}
                    </label>
                    <button
                      class="btn-delete"
                      @click="form.definition.fields.splice(index, 1)"
                    >
                      {{ $t("message.forms_page.delete") }}
                    </button>
                  </div>

                  <div
                    v-if="
                      field.type === 'select' || field.type === 'multiselect'
                    "
                    class="field-options"
                  >
                    <label
                      >{{
                        $t("message.forms_page.options") || "Options"
                      }}:</label
                    >
                    <div
                      v-for="(option, optIndex) in field.options || []"
                      :key="optIndex"
                      class="option-item"
                    >
                      <input
                        v-model="field.options[optIndex]"
                        type="text"
                        class="input-field"
                        placeholder="Option value"
                      />
                      <button
                        class="btn-delete-small"
                        @click="field.options.splice(optIndex, 1)"
                      >
                        ✕
                      </button>
                    </div>
                    <button
                      class="btn-secondary-button btn-small"
                      @click="addFieldOption(field)"
                    >
                      {{ $t("message.forms_page.add_option") || "Add Option" }}
                    </button>
                  </div>
                </div>

                <button
                  class="btn-secondary-button btn-add-field"
                  @click="addField"
                >
                  + {{ $t("message.forms_page.add_field") }}
                </button>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="form-footer">
            <button class="btn-secondary-button" @click="$emit('close')">
              {{ $t("message.forms_page.cancel") }}
            </button>
            <button class="btn-success-button" @click="$emit('save', form)">
              {{ $t("message.forms_page.save_form") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { generateFormWithAI } from "@/api/store/FormsService.js";

export default {
  name: "FormEditorPanel",
  props: {
    form: { type: Object, required: true },
    showEditor: { type: Boolean, required: true }
  },
  data() {
    return {
      showJSONEditor: false,
      jsonEditorText: "",
      aiPrompt: "",
      aiLoading: false,
      aiError: null,
      aiMessages: [],
      claudeHistory: [],
      suggestions: [
        "Create a lead capture form with name, email and phone",
        "Add a dropdown field for truck model interest",
        "Add terms and conditions in Spanish",
        "Make all fields required"
      ]
    };
  },
  watch: {
    "form.id": {
      immediate: false,
      handler() {
        // New/different form opened — reset chat history
        this.aiMessages = [];
        this.claudeHistory = [];
        this.aiError = null;
        this.aiPrompt = "";
      }
    },
    form: {
      deep: true,
      handler(newForm) {
        if (newForm && newForm.definition) {
          this.jsonEditorText = JSON.stringify(newForm.definition, null, 2);
        }
      }
    }
  },
  methods: {
    now() {
      return new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.aiMessages;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    loadJSONFromText() {
      try {
        const parsed = JSON.parse(this.jsonEditorText);
        if (!parsed.fields || !Array.isArray(parsed.fields)) {
          throw new Error("Invalid JSON structure. Must have 'fields' array.");
        }
        this.form.definition = parsed;
        this.$toast.open({
          message: "Form JSON loaded successfully",
          type: "success",
          position: "top"
        });
        this.showJSONEditor = false;
      } catch (e) {
        this.$toast.open({
          message: "Invalid JSON: " + e.message,
          type: "error",
          position: "top"
        });
      }
    },

    async generateWithAI() {
      if (!this.aiPrompt.trim() || this.aiLoading) return;

      const prompt = this.aiPrompt.trim();
      this.aiMessages.push({ role: "user", content: prompt, time: this.now() });
      this.aiPrompt = "";
      this.aiError = null;
      this.aiLoading = true;
      this.scrollChat();

      // On first message, pass form state; afterwards history provides the context
      const isFirstMessage = this.claudeHistory.length === 0;
      const hasCurrentForm =
        this.form.id ||
        (this.form.definition &&
          this.form.definition.fields &&
          this.form.definition.fields.length > 0);

      try {
        const result = await generateFormWithAI(
          prompt,
          isFirstMessage && hasCurrentForm ? this.form : null,
          this.claudeHistory
        );

        // Store the exchange in Claude history for next turn
        this.claudeHistory.push({ role: "user", content: prompt });
        this.claudeHistory.push({ role: "assistant", content: result._raw });

        if (result.name && !this.form.name) {
          this.form.name = result.name;
        }
        this.form.definition = result.definition;

        const fieldCount = result.definition.fields.length;
        const summary = `Updated "${result.name ||
          this.form.name ||
          "form"}" — ${fieldCount} field${fieldCount !== 1 ? "s" : ""}${
          result.definition.termsConditions ? ", with terms & conditions" : ""
        }.`;
        this.aiMessages.push({
          role: "assistant",
          content: summary,
          time: this.now()
        });
      } catch (e) {
        const msg =
          e.response?.data?.error || e.message || "Error generating form.";
        this.aiError = msg;
        this.aiMessages.push({
          role: "assistant",
          content: "⚠ " + msg,
          time: this.now()
        });
      } finally {
        this.aiLoading = false;
        this.scrollChat();
      }
    },

    addField() {
      this.form.definition.fields.push({
        id: "field_" + Date.now(),
        label: "",
        type: "text",
        required: false,
        options: []
      });
    },

    addFieldOption(field) {
      if (!field.options) this.$set(field, "options", []);
      field.options.push("");
    }
  }
};
</script>

<style lang="scss" scoped>
/* ── Overlay ─────────────────────────────────────────── */
.editor-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

/* ── Panel ───────────────────────────────────────────── */
.editor-panel {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 1100px;
  height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

/* ── Header ──────────────────────────────────────────── */
.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #1a1a2e;
  color: #fff;
  flex-shrink: 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
  }

  .close-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    font-size: 26px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    &:hover {
      color: #fff;
    }
  }
}

.ai-star {
  color: #c9a84c;
  font-size: 16px;
}

/* ── Two columns ─────────────────────────────────────── */
.editor-columns {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ── AI Column (left) ────────────────────────────────── */
.ai-column {
  width: 38%;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  background: #0f0f1a;
  border-right: 1px solid #2a2a3e;
}

.ai-col-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #2a2a3e;
  font-size: 13px;
  font-weight: 600;
  color: #c9a84c;
  flex-shrink: 0;
}

.ai-model-tag {
  margin-left: auto;
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.07);
  padding: 2px 8px;
  border-radius: 10px;
}

.ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
}

.ai-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 12px;
  color: rgba(255, 255, 255, 0.3);
  gap: 10px;
  margin: auto 0;
}

.ai-empty-icon {
  font-size: 32px;
  color: rgba(201, 168, 76, 0.3);
}

.ai-empty p {
  font-size: 12px;
  line-height: 1.6;
  margin: 0;
}

.ai-suggestions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  margin-top: 8px;
}

.ai-suggestion {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  padding: 7px 10px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(201, 168, 76, 0.1);
    border-color: rgba(201, 168, 76, 0.3);
    color: rgba(255, 255, 255, 0.8);
  }
}

.ai-msg {
  display: flex;
  flex-direction: column;
  gap: 3px;

  &--user {
    align-items: flex-end;

    .ai-msg__bubble {
      background: linear-gradient(135deg, #7c3aed, #5b21b6);
      color: #fff;
      border-radius: 12px 12px 2px 12px;
    }
  }

  &--assistant {
    align-items: flex-start;

    .ai-msg__bubble {
      background: rgba(255, 255, 255, 0.07);
      color: rgba(255, 255, 255, 0.85);
      border-radius: 12px 12px 12px 2px;
      border: 1px solid rgba(255, 255, 255, 0.08);
    }
  }
}

.ai-msg__bubble {
  max-width: 88%;
  padding: 9px 13px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-msg__time {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.2);
  padding: 0 4px;
}

/* Typing dots */
.ai-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;

  span {
    width: 6px;
    height: 6px;
    background: rgba(255, 255, 255, 0.4);
    border-radius: 50%;
    animation: dot-bounce 1.2s infinite ease-in-out;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes dot-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.7);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.ai-input-area {
  flex-shrink: 0;
  padding: 12px;
  border-top: 1px solid #2a2a3e;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-error {
  padding: 7px 10px;
  background: rgba(220, 38, 38, 0.15);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 6px;
  font-size: 12px;
  color: #fca5a5;
}

.ai-input {
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }
  &:focus {
    outline: none;
    border-color: rgba(124, 58, 237, 0.6);
    background: rgba(255, 255, 255, 0.08);
  }
}

.btn-ai-send {
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 20px;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d28d9, #4c1d95);
    box-shadow: 0 4px 14px rgba(124, 58, 237, 0.4);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.ai-loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ai-spin 0.7s linear infinite;
}

@keyframes ai-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Form Column (right) ─────────────────────────────── */
.form-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
}

.form-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;
  }
}

.form-footer {
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 14px 24px;
  border-top: 1px solid #e0e0e0;
  background: #f9f9f9;
}

/* ── Form controls ───────────────────────────────────── */
.form-group {
  margin-bottom: 18px;

  label {
    display: block;
    margin-bottom: 7px;
    font-size: 12px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
}

.fields-section {
  h3 {
    margin: 0 0 12px 0;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #555;
  }
}

.btn-add-field {
  width: 100%;
  margin-top: 4px;
  text-align: center;
  border: 1px dashed #ccc !important;
  background: transparent !important;
  color: #888 !important;

  &:hover {
    border-color: #aaa !important;
    color: #555 !important;
    background: #f5f5f5 !important;
  }
}

.field-row {
  margin-bottom: 10px;
  padding: 10px 12px;
  background: #f7f7f7;
  border: 1px solid #eee;
  border-radius: 6px;

  &__top {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  .field-item {
    flex: 1;
    min-width: 120px;

    &--type {
      max-width: 140px;
    }
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
    cursor: pointer;
    font-size: 13px;
    flex: 0 0 auto;
  }

  .btn-delete {
    background: #ff4444;
    color: white;
    border: none;
    padding: 6px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    flex: 0 0 auto;

    &:hover {
      background: #cc0000;
    }
  }
}

.field-options {
  margin-top: 10px;
  padding: 10px;
  background: #eff6ff;
  border-radius: 4px;
  border-left: 3px solid #2196f3;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 11px;
    text-transform: uppercase;
    color: #555;
  }
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;

  .input-field {
    flex: 1;
    padding: 6px 8px;
    font-size: 12px;
  }
}

.btn-delete-small {
  background: #ff4444;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  min-width: 28px;

  &:hover {
    background: #cc0000;
  }
}

.input-field,
.select-field {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 13px;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
  }
}

.textarea-field {
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
}

.json-editor-section {
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eee;
}

.json-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #555;

  input[type="checkbox"] {
    cursor: pointer;
  }
}

.json-textarea {
  width: 100%;
  min-height: 300px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
  font-size: 12px;
  line-height: 1.5;
  background: #fafafa;
  box-sizing: border-box;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
  }
}

.btn-small {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-success-button,
.btn-secondary-button {
  padding: 8px 18px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-success-button {
  background: #4caf50;
  color: white;
  &:hover {
    background: #45a049;
  }
}

.btn-secondary-button {
  background: #e0e0e0;
  color: #333;
  &:hover {
    background: #d0d0d0;
  }
}

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 768px) {
  .editor-overlay {
    padding: 0;
  }

  .editor-panel {
    height: 100vh;
    border-radius: 0;
    max-width: 100%;
  }

  .editor-columns {
    flex-direction: column;
  }

  .ai-column {
    width: 100%;
    min-width: unset;
    height: 40vh;
    border-right: none;
    border-bottom: 1px solid #2a2a3e;
  }

  .form-column {
    height: 60vh;
  }
}
</style>
